import classNames from 'classnames';
import { isEmpty, isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { Button, Divider, Image, MessagePlugin, Popconfirm, Textarea } from 'tdesign-react';

import { ColorPickerRichMedia } from '@/components/colorPicker';
import NovaDialog from '@/components/dialog';
import { AddDown, Crop, OutPaintIcon, Ratio11, Ratio34, Ratio43, Ratio169, Ratio916, RatioOrigin, RemoveBg, Up } from '@/components/icon';
import { BasicImageViewer } from '@/components/review/image';
import { LIMIT_PIC } from '@/const/form';
import { MediaConfigType } from '@/const/richMedia';
import { OutPaintReq, OutPaintType, RemoveBackgroundReq, RemoveBackgroundResp } from '@/interface/aigc';
import { RichMediaValue } from '@/interface/media';
import CropBar from '@/material/cropModal';
import { outPaint, removeBackground } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { isPic, isWoa } from '@/utils/getApiDomain';
import { getImageSizeAsync } from '@/utils/getImageSize';

import Rename from '../../components/rename';
import { RichMedia } from '../../components/rich-media';
import { IFileInfo, ImageRatioType } from '..';
import { updateName } from '../utils/updateName';
import { isPicType } from '../utils/validate';
import styles from './index.module.less';

interface ImageDialogProps {
  config?: any;
  visible: boolean;
  imageInfo: IFileInfo;
  ratio: ImageRatioType;
  isCrop: boolean;
  disabled: boolean;
  isHorizontal: boolean;
  fileType?: string[]; // 支持的格式类型
  isCloseGetColor?: boolean; // 是否关闭获取颜色
  onClose: () => void;
  onConfirm: (value: any) => void;
}

interface IResolution {
  label: string;
  value: { width: number; height: number };
  icon: React.ReactNode;
}

const MIN_SIZE = 256; // 抠图限制 [256, 4096]
const MAX_SIZE = 4096;
const OUT_PAINT_TIP = '只扩充背景，图中的人物角色保持不变，扩图过程中，去除两侧的分割线';
const OUT_PAINT_PLACEHOLDER = isPic
  ? OUT_PAINT_TIP
  : '描述想要扩图的内容，不填将基于原图生成';
const OUT_PAINT_PROMPT = isPic
  ? OUT_PAINT_TIP
  : '';

const RESOLUTION_OPTION: IResolution[] = [
  {
    label: '原比例',
    value: { width: 0, height: 0 },
    icon: <RatioOrigin />,
  },
  {
    label: '16:9',
    value: { width: 16, height: 9 },
    icon: <Ratio169 />,
  },
  {
    label: '4:3',
    value: { width: 4, height: 3 },
    icon: <Ratio43 />,
  },
  {
    label: '1:1',
    value: { width: 1, height: 1 },
    icon: <Ratio11 />,
  },
  {
    label: '3:4',
    value: { width: 3, height: 4 },
    icon: <Ratio34 />,
  },
  {
    label: '9:16',
    value: { width: 9, height: 16 },
    icon: <Ratio916 />,
  },
];

const ImageDialog: React.FC<ImageDialogProps> = ({
  config,
  visible,
  imageInfo,
  ratio,
  isCrop,
  disabled,
  isHorizontal,
  fileType,
  isCloseGetColor = false,
  onClose,
  onConfirm,
}) => {
  const [url, setUrl] = useState<string>(''); // 当前展示的图片
  const [originUrl, setOriginUrl] = useState<string>(''); // 扩图所用的原始图片，可以多次尝试抽卡
  const [oldUrl, setOldUrl] = useState<string>(''); // 抠图用，前后对比
  const [media, setMedia] = useState(imageInfo.media);
  const [sample, setSample] = useState<RichMediaValue['sample']>(imageInfo.sample);
  const [name, setName] = useState<string | undefined>(imageInfo.name);
  const [resolution, setResolution] = useState({ width: 0, height: 0 });
  const [outpaintPrompt, setOutPrompt] = useState(OUT_PAINT_PROMPT);

  const [showSetting, setShowSetting] = useState(false);
  const [showCrop, setShowCrop] = useState(isCrop);
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const [paintLoading, setPaintLoading] = useState(false);
  const [colorLoading, setColorLoading] = useState(false);
  const colorRef = useRef<any>(null);

  useEffect(() => {
    if (!visible) {
      setUrl(imageInfo.url || '');
      setOriginUrl(imageInfo.url || '');
      setMedia(imageInfo.media);
      setSample(imageInfo.sample);
      setName(imageInfo.name || '未命名');
      setOldUrl('');
      setOutPrompt(OUT_PAINT_PROMPT);
    }
  }, [imageInfo, visible]);

  useEffect(() => {
    setShowCrop(isCrop);
  }, [isCrop, visible]);

  // 取色
  const getColorSample = async (url: string) => {
    if (!url || isCloseGetColor) return;
    const sample = await colorRef.current?.getColor(url);
    setColorLoading(false);
    if (isEmpty(sample)) {
      return;
    }
    setSample(sample);
  };

  // 抠图
  const rmBackground = async () => {
    if (!url) return;

    const isGif = url?.match(/\.(gif)$/i);
    if (isGif) {
      MessagePlugin.error('暂不支持gif格式');
      return;
    }

    const { width, height } = await getImageSizeAsync(url);
    if (width < MIN_SIZE || height < MIN_SIZE) {
      MessagePlugin.error(`图片尺寸不能小于${MIN_SIZE}像素`);
      return;
    }
    if (width > MAX_SIZE || height > MAX_SIZE) {
      MessagePlugin.error(`图片尺寸不能大于${MAX_SIZE}像素`);
      return;
    }

    setRemoveLoading(true);
    const req: RemoveBackgroundReq = { image_url: url };
    const { code, data, message } = await removeBackground(req) as CgiResp<RemoveBackgroundResp>;
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '抠图失败，请点击重试',
      });
      setRemoveLoading(false);
      return;
    }

    const { after_process_url: newUrl } = data;
    if (!newUrl) {
      MessagePlugin.error('抠图失败，请点击重试');
      setRemoveLoading(false);
      return;
    }
    setOldUrl(url);
    setUrl(newUrl);
    setOriginUrl(newUrl);
    setName(updateName(name));
    setRemoveLoading(false);
    getColorSample(newUrl);
  };

  // 扩图
  const getOutPaint = async () => {
    if (!originUrl) return;

    if (!isPicType(originUrl, fileType || LIMIT_PIC.type)) {
      return;
    }

    setPaintLoading(true);
    const { width, height } = await getImageSizeAsync(originUrl);
    let outputWidth = resolution.width;
    let outputHeight = resolution.height;

    if (resolution.width === 0 && resolution.height === 0) {
      // 原比例时设置为原图尺寸的1.5倍
      outputWidth = Math.ceil(width * 1.5);
      outputHeight = Math.ceil(height * 1.5);
    }

    const req: OutPaintReq = {
      image_url: originUrl,
      custom_prompt: outpaintPrompt,
      input_Ratio: { base_width: width, base_height: height },
      output_Ratio: {
        base_width: outputWidth,
        base_height: outputHeight,
      },
      type: OutPaintType.OUTPAINT_TYPE_OUTPAINTING,
    };
    const { code, data, message } = await outPaint(req);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || '扩图失败，请点击重试',
      });
      setPaintLoading(false);
      return;
    }

    const { url: newUrl } = data;
    if (!newUrl) {
      MessagePlugin.error('扩图失败，请点击重试');
      setPaintLoading(false);
      return;
    }

    setOldUrl('');
    setUrl(newUrl);
    setName(updateName(name));
    setPaintLoading(false);
    getColorSample(newUrl);
  };

  const openCrop = () => {
    const isGif = url?.match(/\.(gif)$/i);
    if (isGif) {
      MessagePlugin.error('暂不支持gif格式');
      return;
    }
    setShowCrop(true);
  };

  const handleCrop = async (cropUrl: string) => {
    if (!cropUrl) {
      MessagePlugin.error('裁剪失败,请点击重试');
      return;
    }
    setUrl(cropUrl);
    setOriginUrl(cropUrl);
    setName(updateName(name));
    setShowCrop(false);
    getColorSample(cropUrl);
  };

  const handleConfirm = async () => {
    onConfirm({ ...imageInfo, url, name, sample, media });
  };

  const getHeader = () => (
    <div className={styles.modalTitle}>
      <span style={{ flexGrow: 1 }}>图片编辑</span>
    </div>
  );

  const dialogConfig = {
    ...config,
    visible,
    closeBtn: false,
    className: styles.dialog,
    width: 'fit-content',
    height: 500,
    onCancel: onClose,
    onConfirm: handleConfirm,
    destroyOnClose: true,
    header: getHeader(),
  };

  return (
    <NovaDialog
      dialogConfig={dialogConfig}
      confirmBtnDisabled={colorLoading || showCrop || removeLoading || paintLoading}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ width: 'fit-content', maxWidth: 300 }}>
          <Rename
            value={name || '未命名'}
            disabled={disabled}
            onChange={setName}
          />
        </div>
        {!showCrop && <div className={classNames(styles.wrapper, { [styles.wrapperHorizon]: isHorizontal })}>
          <div className={classNames(styles.left, { [styles.up]: isHorizontal })}>
            {oldUrl === '' ? <BasicImageViewer images={[url]}>
              <Image
                src={url}
                fit={'contain'}
                style={{ width: '100%', height: '100%' }}
                loading={'...'}
              />
            </BasicImageViewer>
              : <ReactCompareSlider
                className={styles.slider}
                itemOne={<ReactCompareSliderImage src={oldUrl} alt="Image one" />}
                itemTwo={<ReactCompareSliderImage src={url} alt="Image two" style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: 'conic-gradient(#eee 0 25%, #ccc 0 50%, #eee 0 75%, #ccc 0)',
                  backgroundSize: '15px 15px',
                }} />}
              />}
          </div>
          <div className={classNames(styles.right, { [styles.down]: isHorizontal })}>
            <Button
              theme='default'
              style={{ width: 80, height: 32 }}
              disabled={disabled}
              onClick={openCrop}
            >
              <div className={styles.btnText}>
                <Crop />{!showCrop ? '裁剪' : '取消裁剪'}
              </div>
            </Button>
            <Button
              theme='default'
              style={{ width: 80, height: 32 }}
              disabled={disabled}
              onClick={rmBackground}
              loading={removeLoading}
            >
              <div className={styles.btnText}>
                {!removeLoading && <RemoveBg />}
                抠图
              </div>
            </Button>
            <Popconfirm
              placement={'top'}
              icon={<></>}
              className={styles.popconfirm}
              content={
                <div className={styles.outpaint}>
                  <Textarea
                    autosize={{ minRows: 2, maxRows: 2 }}
                    style={{ width: 271 }}
                    maxlength={100}
                    placeholder={OUT_PAINT_PLACEHOLDER}
                    value={outpaintPrompt}
                    onChange={setOutPrompt}
                  />
                  <div className={styles.resolution}>
                    {RESOLUTION_OPTION.map(e => <div
                      key={e.label}
                      className={classNames(
                        styles.item,
                        { [styles.active]: isEqual(e.value, resolution) },
                      )}
                      onClick={() => setResolution(e.value)}
                    >
                      {e.icon}
                      <span className={styles.label}>{e.label}</span>
                    </div>)}
                  </div>
                </div>
              }
              onConfirm={getOutPaint}
            >
              <Button
                theme='default'
                style={{ width: 80, height: 32 }}
                disabled={disabled}
                loading={paintLoading}
              >
                <div className={styles.btnText}>
                  {!paintLoading && <OutPaintIcon />}
                  扩图
                </div>
              </Button>
            </Popconfirm>
          </div>
        </div>}

        {showCrop && <CropBar
          url={url}
          name={name || ''}
          isHorizontal={isHorizontal}
          corpItem={{
            cropContainerWidth: isHorizontal ? 348 : 220,
            cropContainerHeight: isHorizontal ? 220 : 308,
            cropRatio: (ratio && 'width' in ratio)
              ? ratio.width / ratio.height
              : undefined,
          }}
          onClose={() => setShowCrop(false)}
          onConfirm={handleCrop}
        />}

        {isWoa ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Divider align="center" >
              <div onClick={() => setShowSetting(!showSetting)} className={styles.settingText}>
                <span>高级设置</span>
                {showSetting ? <Up /> : <AddDown />}
              </div>
            </Divider>
            {showSetting && <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <RichMedia
                value={media}
                richMedia={{ type: MediaConfigType.PIC }}
                onChange={(key, val) => setMedia({ ...media, [key]: val })}
              />
              <ColorPickerRichMedia
                colorRef={colorRef}
                value={sample}
                url={url}
                isShowColorPicker={true}
                onChange={v => setSample(v)}
                getColorStatus={v => setColorLoading(v)}
                disabledBtn={showCrop || removeLoading || paintLoading}
              />
            </div>}
          </div>
        ) : <ColorPickerRichMedia
          colorRef={colorRef}
          value={sample}
            url={url}
          isShowColorPicker={false}
          onChange={v => setSample(v)}
          getColorStatus={v => setColorLoading(v)}
            disabledBtn={showCrop || removeLoading || paintLoading}
        />}
      </div>
    </NovaDialog>
  );
};

export default ImageDialog;
